import axios from "axios";
import {
  CreateProductDatasheetUploadUrlDtoContentTypeEnum,
  type CreateProductDatasheetUploadUrlDto,
} from "../../../api";
import { productDatasheetsService } from "../../api_client";

const PDF_CONTENT_TYPE = CreateProductDatasheetUploadUrlDtoContentTypeEnum.ApplicationPdf;

export class ProductDatasheetUploadError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "ProductDatasheetUploadError";
  }
}

export interface UploadProductDatasheetOptions {
  scope?: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export interface UploadProductDatasheetResult {
  objectKey: string;
  fileName: string;
}

/**
 * Uploads a PDF datasheet to GCS via the backend's signed-URL flow.
 *
 *   1. POST /product-datasheets/upload-url → { uploadUrl, headers, objectKey }
 *   2. PUT the raw binary directly to GCS using `uploadUrl`
 *   3. Return `{ objectKey, fileName }` — the caller stores them on
 *      CreateProductRequestDto.datasheetObjectKey + datasheetFileName.
 */
export async function uploadProductDatasheet(
  file: File,
  options?: UploadProductDatasheetOptions,
): Promise<UploadProductDatasheetResult> {
  if (file.type !== PDF_CONTENT_TYPE) {
    throw new ProductDatasheetUploadError(
      `Unsupported datasheet type: ${file.type || "unknown"}. Only PDF is accepted.`,
    );
  }

  const dto: CreateProductDatasheetUploadUrlDto = {
    contentType: PDF_CONTENT_TYPE,
    fileSize: file.size,
    ...(options?.scope ? { scope: options.scope } : {}),
  };

  let urlRes;
  try {
    urlRes = await productDatasheetsService.createUploadUrl(dto);
  } catch (err) {
    throw new ProductDatasheetUploadError("Could not get upload URL", err);
  }

  const { uploadUrl, headers, objectKey, maxBytes } = urlRes.data;

  if (file.size > maxBytes) {
    throw new ProductDatasheetUploadError(
      `Datasheet exceeds the ${(maxBytes / 1024 / 1024).toFixed(0)}MB size limit.`,
    );
  }

  try {
    await axios.put(uploadUrl, file, {
      headers: { ...headers, "Content-Type": PDF_CONTENT_TYPE },
      withCredentials: false,
      transformRequest: [(d) => d],
      signal: options?.signal,
      onUploadProgress: (e) => {
        if (!options?.onProgress) return;
        const total = e.total ?? file.size;
        if (!total) return;
        options.onProgress(Math.min(100, Math.round((e.loaded / total) * 100)));
      },
    });
  } catch (err) {
    throw new ProductDatasheetUploadError("Could not upload datasheet to storage", err);
  }

  return { objectKey, fileName: file.name };
}
